import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { progressApi } from '@/api/progress';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { useIsMobile } from '@/hooks/useIsMobile';
import { humanizeError } from '@/lib/errors';
import { type CuratorGrade } from '@/lib/lessonFeedback';
import { manualActionErrorText } from '@/lib/lessonManualActions';

/**
 * Оценка и фидбек куратора за урок одной формой (D11, 10.09.2026).
 *
 * ⚠️ СОХРАНЯЮТСЯ ВМЕСТЕ, ОДНОЙ РУЧКОЙ, ПО КНОПКЕ. Прежнее поле оценки писало по
 * уходу из поля — для многострочного текста это не годится: куратор щёлкнет
 * мимо посреди фразы, и полуфраза уйдёт ученику уведомлением.
 *
 * ⚠️ ПУСТАЯ ОЦЕНКА И НОЛЬ — РАЗНЫЕ СОСТОЯНИЯ, как и в прежнем поле: пусто —
 * «не оценивали» (`grade: null`), ноль — настоящая оценка.
 *
 * ⚠️ У НЕПРОЙДЕННОГО УРОКА ФОРМА ВЫКЛЮЧЕНА: сервер такую оценку отвергнет.
 *
 * Показывается только при `grading_mode === 'curator'` и только если сервер уже
 * присылает `curator_grade` — решает вызывающий (`StudentProgressPage`).
 */
export function LessonFeedbackEditor({
  studentId,
  lessonId,
  lessonTitle,
  curatorGrade,
  isCompleted,
  canComplete = false,
}: {
  studentId: string;
  lessonId: string;
  lessonTitle: string;
  curatorGrade: CuratorGrade | null;
  isCompleted: boolean;
  /**
   * Ученик стоит на этом уроке, и оценка засчитает его (договор `lesson-force-complete.md` §2,
   * решение владельца 13.09.2026): у непройденного урока форма включается. Решает вызывающий —
   * по `stuckLessonIds`, только на уроке, где ученик и стоит.
   */
  canComplete?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const ask = useConfirm();
  // На телефоне кнопки 44 px — норма касания (docs/CURATOR-MOBILE-DESIGN.md §1), на компьютере прежние.
  const btnSize = useIsMobile() ? 'md' : 'sm';

  const asText = (v?: number | null) => (v === null || v === undefined ? '' : String(v));
  const savedGrade = asText(curatorGrade?.grade);
  const savedFeedback = curatorGrade?.feedback ?? '';

  const [grade, setGrade] = useState(savedGrade);
  const [feedback, setFeedback] = useState(savedFeedback);
  const [error, setError] = useState('');
  const [justSaved, setJustSaved] = useState(false);
  /** Этим сохранением урок засчитан — сервер сказал `completed_now`. */
  const [completedNow, setCompletedNow] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Форма зачёта свёрнута до нажатия: иначе под уроком, где ученик стоит, всегда висели бы
  // пустые поля — та же «стена форм», что убирали 10.09.
  const [completeOpen, setCompleteOpen] = useState(false);

  // Список перечитывается после сохранения — подхватываем то, что записал
  // сервер, а не то, что набрали.
  useEffect(() => {
    setGrade(savedGrade);
    setFeedback(savedFeedback);
  }, [savedGrade, savedFeedback]);

  const dirty = grade.trim() !== savedGrade || feedback.trim() !== savedFeedback.trim();

  const save = useMutation({
    // ⚠️ Оба поля пусты — снимаем оценку, а не сохраняем пустоту. По договору
    // (`docs/CONTRACTS/lesson-feedback.md`) каждое сохранение шлёт ученику
    // уведомление «Куратор оценил урок», а снятие — нет.
    mutationFn: async (body: { grade: number | null; feedback: string | null }) => {
      if (body.grade === null && body.feedback === null) {
        await progressApi.clearLessonGrade(studentId, lessonId);
        return null;
      }
      return progressApi.saveLessonFeedback(studentId, lessonId, body);
    },
    onSuccess: (res) => {
      setError('');
      setJustSaved(true);
      setCompletedNow(res?.completed_now === true);
      qc.invalidateQueries({ queryKey: ['staff', 'student-lessons', studentId] });
      qc.invalidateQueries({ queryKey: ['staff', 'lesson-grade-history', studentId, lessonId] });
    },
    onError: (e) => {
      setJustSaved(false);
      // Разные 409 различаются по code (`lib/lessonManualActions.ts`): курс перевели в
      // автоматический режим, урок уже пройден, засчитывать без цифры нечего.
      setError(manualActionErrorText(e, t));
    },
  });

  const submit = async () => {
    const trimmed = grade.trim();
    let value: number | null = null;
    // Проверяем здесь же: отказ должен быть виден сразу у поля.
    if (trimmed !== '') {
      value = Number(trimmed);
      if (!Number.isInteger(value) || value < 0 || value > 100) {
        setError(t('studentProgress.gradeRange'));
        return;
      }
    }
    const text = feedback.trim();
    // ⚠️ ЗАЧЁТ — ТОЛЬКО С ЦИФРОЙ И ПОСЛЕ ПОДТВЕРЖДЕНИЯ. Без цифры сервер ответит
    // `409 lesson_not_completed`, а отмены зачёта нет вовсе (договор §9): одно неосторожное
    // «Сохранить» навсегда сделало бы урок пройденным.
    if (!isCompleted) {
      if (value === null) {
        setError(t('studentProgress.errNeedsGrade'));
        return;
      }
      // ⚠️ УРОК БЕЗ ОТВЕТОВ — ОТДЕЛЬНЫЙ ВОПРОС (26.09.2026, вариант В разбора
      // CHECK-2026-09-26-KHABIBULLA-ANSWERS.md). Кураторы засчитывали уроки, которых ученик не
      // открывал, и потом искали «пропавшие» ответы. Ответы берём свежие, той же ручкой, что и
      // разбор урока. Не удалось узнать — прежний вопрос: зачёт возможен и сегодня, лишнего
      // запрета не вводим.
      const noAnswers = await progressApi
        .getStudentLessonAnswers(studentId, lessonId)
        // Пустой список Go-сервер может отдать и как null.
        .then((answers) => answers == null || (Array.isArray(answers) && answers.length === 0))
        .catch(() => false);
      const confirmed = await ask(
        noAnswers
          ? {
              title: t('studentProgress.completeNoAnswersTitle'),
              message: t('studentProgress.completeNoAnswers', { title: lessonTitle, grade: value }),
              confirmLabel: t('studentProgress.completeConfirmButton'),
              danger: true,
            }
          : {
              title: t('studentProgress.completeConfirmTitle'),
              message: t('studentProgress.completeConfirm', { title: lessonTitle, grade: value }),
              confirmLabel: t('studentProgress.completeConfirmButton'),
            },
      );
      if (!confirmed) return;
    }
    save.mutate({ grade: value, feedback: text === '' ? null : text });
  };

  const history = useQuery({
    queryKey: ['staff', 'lesson-grade-history', studentId, lessonId],
    queryFn: () => progressApi.getLessonGradeHistory(studentId, lessonId),
    enabled: historyOpen,
  });

  // Поля доступны у пройденного урока и у урока, где ученик стоит (зачёт оценкой).
  const canEdit = isCompleted || canComplete;
  const disabled = !canEdit || save.isPending;

  // ⚠️ Непройденный урок без оценки — одной строкой, а не выключенной формой.
  // Формы под каждым уроком курса на 25 уроков превращали страницу в стену
  // пустых серых полей (замечено на стенде 10.09.2026).
  if (!isCompleted && !curatorGrade && !(canComplete && completeOpen)) {
    // Ученик стоит на этом уроке — вместо строки кнопка, раскрывающая форму зачёта.
    if (canComplete) {
      return (
        <Button size={btnSize} variant="secondary" onClick={() => setCompleteOpen(true)}>
          <span className="material-symbols-outlined">task_alt</span>
          {t('studentProgress.completeOpen')}
        </Button>
      );
    }
    return <p className="text-xs text-on-surface-variant">{t('studentProgress.gradeAfterCompletion')}</p>;
  }

  return (
    <div className="space-y-2.5 rounded-card bg-surface-container-low p-3">
      <div className="flex flex-wrap items-start gap-3">
        <label className="flex flex-col gap-1" htmlFor={`lesson-grade-${lessonId}`}>
          <span className="text-xs text-on-surface-variant">{t('studentProgress.grade')}</span>
          <input
            id={`lesson-grade-${lessonId}`}
            type="number"
            min={0}
            max={100}
            inputMode="numeric"
            value={grade}
            disabled={disabled}
            placeholder={t('studentProgress.notGraded')}
            onChange={(e) => {
              setGrade(e.target.value);
              setJustSaved(false);
              // Прежняя подсказка («поставьте оценку») не должна висеть, когда цифру уже ввели.
              setError('');
            }}
            className="h-11 w-20 rounded-button border border-outline-variant bg-surface-container-lowest px-2 text-right text-sm tabular-nums text-on-surface disabled:opacity-40"
          />
        </label>
        <label className="flex min-w-[14rem] flex-1 flex-col gap-1" htmlFor={`lesson-feedback-${lessonId}`}>
          <span className="text-xs text-on-surface-variant">{t('studentProgress.feedback')}</span>
          <textarea
            id={`lesson-feedback-${lessonId}`}
            rows={2}
            maxLength={5000}
            value={feedback}
            disabled={disabled}
            placeholder={t('studentProgress.feedbackPlaceholder')}
            onChange={(e) => {
              setFeedback(e.target.value);
              setJustSaved(false);
              // Прежняя подсказка («поставьте оценку») не должна висеть, когда цифру уже ввели.
              setError('');
            }}
            className="min-h-[2.75rem] w-full resize-y rounded-button border border-outline-variant bg-surface-container-lowest px-3 py-2 text-sm text-on-surface disabled:opacity-40"
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Button size={btnSize} onClick={submit} disabled={!canEdit || !dirty} loading={save.isPending}>
          {isCompleted ? t('studentProgress.saveFeedback') : t('studentProgress.completeConfirmButton')}
        </Button>
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          className="inline-flex min-h-11 items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          <span className="material-symbols-outlined text-[16px]" aria-hidden="true">history</span>
          {t('studentProgress.history')}
        </button>
        {justSaved && !dirty && (
          <span className="text-xs text-success">
            {completedNow ? t('studentProgress.completedNow') : t('studentProgress.saved')}
          </span>
        )}
        {!isCompleted && (
          <span className="basis-full text-xs text-on-surface-variant">
            {canComplete ? t('studentProgress.completeHint') : t('studentProgress.gradeAfterCompletion')}
          </span>
        )}
        {error && <span className="text-xs text-error">{error}</span>}
      </div>

      <Modal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={t('studentProgress.historyTitle', { title: lessonTitle })}
        phoneFull
      >
        {history.isLoading ? (
          <p className="text-sm text-on-surface-variant">{t('common.loading')}</p>
        ) : history.isError ? (
          <p className="text-sm text-error">{humanizeError(history.error)}</p>
        ) : !history.data?.length ? (
          <p className="text-sm text-on-surface-variant">{t('studentProgress.historyEmpty')}</p>
        ) : (
          <ol className="divide-y divide-outline-variant/60">
            {history.data.map((h) => (
              <li key={`${h.changed_at}-${h.changed_by?.id ?? ''}`} className="space-y-1 py-3">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className="font-semibold tabular-nums text-on-surface">
                    {h.grade === null ? t('studentProgress.notGraded') : `${h.grade}/100`}
                  </span>
                  <span className="text-xs text-on-surface-variant">
                    {t('studentProgress.changedBy', {
                      name: h.changed_by?.name || t('studentProgress.unknownCurator'),
                      date: new Date(h.changed_at).toLocaleString(i18n.language),
                    })}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-sm text-on-surface">
                  {h.feedback ?? t('studentProgress.historyNoFeedback')}
                </p>
              </li>
            ))}
          </ol>
        )}
      </Modal>
    </div>
  );
}
